import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, TrendingUp, Calendar, Dumbbell, Flame } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';
import HamburgerMenu from '../components/HamburgerMenu';
import { getWorkoutTypeLabel } from '../utils/workoutTypes';

const Progress = ({ user, onLogout }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [weightLifted, setWeightLifted] = useState(null);
  const [caloriesBurned, setCaloriesBurned] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedExercise, setSelectedExercise] = useState(null);

  useEffect(() => {
    loadProgressData();
  }, []);

  const loadProgressData = async () => {
    setLoading(true);
    try {
      const [statsResponse, historyResponse, weightLiftedResponse, caloriesResponse] = await Promise.all([
        axios.get(`${API}/progress/stats?days=30`),
        axios.get(`${API}/workouts/history?limit=50`),
        axios.get(`${API}/progress/weight-lifted`),
        axios.get(`${API}/progress/calories-burned`)
      ]);
      setStats(statsResponse.data);
      setHistory(historyResponse.data);
      setWeightLifted(weightLiftedResponse.data);
      setCaloriesBurned(caloriesResponse.data);

      // Set first exercise with progress data as selected
      const exerciseNames = Object.keys(statsResponse.data.exercise_progress);
      if (exerciseNames.length > 0) {
        setSelectedExercise(exerciseNames[0]);
      }
    } catch (error) {
      toast.error('Failed to load progress data');
      console.error('Error loading progress:', error);
    } finally {
      setLoading(false);
    }
  };

  const getChartData = () => {
    if (!selectedExercise || !stats?.exercise_progress[selectedExercise]) return [];
    
    return stats.exercise_progress[selectedExercise]
      .map(item => ({
        date: new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
        weight: item.weight
      }))
      .reverse();
  };

  const getWorkoutTypeData = () => {
    if (!stats?.workout_by_type) return [];
    return Object.entries(stats.workout_by_type).map(([type, count]) => ({
      name: type === 'home' ? 'Home' : 'Gym',
      count
    }));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black">
        <HamburgerMenu />
        <div className="flex items-center justify-center pt-20">
          <div className="text-blood-400 text-xl font-bold tracking-wider">LOADING BATTLE RECORDS...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-stone-950 via-stone-900 to-black pb-20 overflow-x-hidden">
      <HamburgerMenu />
      
      <div className="max-w-7xl mx-auto px-4 py-4 animate-fade-in">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-blood-400 mb-1 tracking-wider" data-testid="progress-title">
            YOUR BATTLE RECORDS
          </h1>
          <p className="text-stone-500 uppercase tracking-widest text-xs">Witness your legendary progress</p>
        </div>

        <Tabs defaultValue="overview" className="space-y-6">
          <TabsList className="glass inline-flex p-1 rounded-xl border border-stone-800">
            <TabsTrigger value="overview" data-testid="overview-tab" className="data-[state=active]:bg-blood-900">Overview</TabsTrigger>
            <TabsTrigger value="charts" data-testid="charts-tab" className="data-[state=active]:bg-blood-900">Charts</TabsTrigger>
            <TabsTrigger value="history" data-testid="history-tab" className="data-[state=active]:bg-blood-900">History</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-6" data-testid="overview-content">
            {/* Weight Lifted Stats - Featured Section */}
            {weightLifted && (
              <Card className="glass p-8 border-2 border-blood-700 bg-gradient-to-br from-blood-950/20 to-stone-950">
                <h2 className="text-3xl font-bold text-blood-400 mb-6 tracking-wider flex items-center">
                  <Dumbbell className="w-8 h-8 mr-3 text-blood-500" />
                  TOTAL WEIGHT LIFTED
                </h2>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="glass p-4 rounded-lg border border-stone-700">
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Today</p>
                    <p className="text-2xl md:text-3xl font-bold text-blood-400">
                      {Math.round(weightLifted.daily).toLocaleString()}
                    </p>
                    <p className="text-stone-500 text-xs mt-1">{user?.weight_unit || 'lbs'}</p>
                  </div>
                  <div className="glass p-4 rounded-lg border border-stone-700">
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">This Week</p>
                    <p className="text-2xl md:text-3xl font-bold text-blood-400">
                      {Math.round(weightLifted.weekly).toLocaleString()}
                    </p>
                    <p className="text-stone-500 text-xs mt-1">{user?.weight_unit || 'lbs'}</p>
                  </div>
                  <div className="glass p-4 rounded-lg border border-stone-700">
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">This Month</p>
                    <p className="text-2xl md:text-3xl font-bold text-blood-400">
                      {Math.round(weightLifted.monthly).toLocaleString()}
                    </p>
                    <p className="text-stone-500 text-xs mt-1">{user?.weight_unit || 'lbs'}</p>
                  </div>
                  <div className="glass p-4 rounded-lg border border-stone-700">
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">This Year</p>
                    <p className="text-2xl md:text-3xl font-bold text-blood-400">
                      {Math.round(weightLifted.yearly).toLocaleString()}
                    </p>
                    <p className="text-stone-500 text-xs mt-1">{user?.weight_unit || 'lbs'}</p>
                  </div>
                  <div className="glass p-4 rounded-lg border-2 border-blood-600 bg-blood-950/30">
                    <p className="text-blood-300 text-xs uppercase tracking-wider mb-1 font-bold">All Time</p>
                    <p className="text-2xl md:text-3xl font-bold text-blood-400">
                      {Math.round(weightLifted.all_time).toLocaleString()}
                    </p>
                    <p className="text-blood-500 text-xs mt-1">{user?.weight_unit || 'lbs'} ⚔️</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Calories Burned Stats — workouts + manual activities + yoga rolled up */}
            {caloriesBurned && (
              <Card className="glass p-8 border-2 border-orange-700 bg-gradient-to-br from-orange-950/20 to-stone-950" data-testid="calories-burned-card">
                <h2 className="text-3xl font-bold text-orange-400 mb-6 tracking-wider flex items-center">
                  <Flame className="w-8 h-8 mr-3 text-orange-500" />
                  TOTAL CALORIES BURNED
                </h2>
                <p className="text-stone-400 text-xs uppercase tracking-wider mb-4 -mt-3">
                  Workouts · Yoga / Pilates · Outdoor Activities
                </p>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="glass p-4 rounded-lg border border-stone-700" data-testid="calories-daily">
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">Today</p>
                    <p className="text-2xl md:text-3xl font-bold text-orange-400">
                      {Math.round(caloriesBurned.daily).toLocaleString()}
                    </p>
                    <p className="text-stone-500 text-xs mt-1">cal</p>
                  </div>
                  <div className="glass p-4 rounded-lg border border-stone-700" data-testid="calories-weekly">
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">This Week</p>
                    <p className="text-2xl md:text-3xl font-bold text-orange-400">
                      {Math.round(caloriesBurned.weekly).toLocaleString()}
                    </p>
                    <p className="text-stone-500 text-xs mt-1">cal</p>
                  </div>
                  <div className="glass p-4 rounded-lg border border-stone-700" data-testid="calories-monthly">
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">This Month</p>
                    <p className="text-2xl md:text-3xl font-bold text-orange-400">
                      {Math.round(caloriesBurned.monthly).toLocaleString()}
                    </p>
                    <p className="text-stone-500 text-xs mt-1">cal</p>
                  </div>
                  <div className="glass p-4 rounded-lg border border-stone-700" data-testid="calories-yearly">
                    <p className="text-stone-400 text-xs uppercase tracking-wider mb-1">This Year</p>
                    <p className="text-2xl md:text-3xl font-bold text-orange-400">
                      {Math.round(caloriesBurned.yearly).toLocaleString()}
                    </p>
                    <p className="text-stone-500 text-xs mt-1">cal</p>
                  </div>
                  <div className="glass p-4 rounded-lg border-2 border-orange-600 bg-orange-950/30" data-testid="calories-all-time">
                    <p className="text-orange-300 text-xs uppercase tracking-wider mb-1 font-bold">All Time</p>
                    <p className="text-2xl md:text-3xl font-bold text-orange-400">
                      {Math.round(caloriesBurned.all_time).toLocaleString()}
                    </p>
                    <p className="text-orange-500 text-xs mt-1">cal 🔥</p>
                  </div>
                </div>
              </Card>
            )}

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="glass p-6 card-hover border border-stone-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blood-300 text-sm mb-1 uppercase tracking-wider">Total Battles</p>
                    <p className="text-4xl font-bold text-white" data-testid="total-workouts-stat">{stats?.total_workouts || 0}</p>
                  </div>
                  <Dumbbell className="w-12 h-12 text-blood-600" />
                </div>
                <p className="text-stone-400 text-sm mt-4 uppercase tracking-wide">Last 30 days</p>
              </Card>

              <Card className="glass p-6 card-hover border border-stone-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blood-300 text-sm mb-1 uppercase tracking-wider">War Streak</p>
                    <p className="text-4xl font-bold text-white">{stats?.total_workouts >= 12 ? '🔥' : '⚔️'}</p>
                  </div>
                  <Calendar className="w-12 h-12 text-blood-600" />
                </div>
                <p className="text-stone-400 text-sm mt-4 uppercase tracking-wide">
                  {stats?.total_workouts >= 12 ? 'Legendary consistency!' : 'Keep training!'}
                </p>
              </Card>

              <Card className="glass p-6 card-hover border border-stone-800">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blood-300 text-sm mb-1 uppercase tracking-wider">Exercises Mastered</p>
                    <p className="text-4xl font-bold text-white">{Object.keys(stats?.exercise_progress || {}).length}</p>
                  </div>
                  <TrendingUp className="w-12 h-12 text-blood-600" />
                </div>
                <p className="text-stone-400 text-sm mt-4 uppercase tracking-wide">Movement library</p>
              </Card>
            </div>

            {/* Workout Distribution */}
            {stats?.workout_by_type && Object.keys(stats.workout_by_type).length > 0 && (
              <Card className="glass p-6 border border-stone-800">
                <h2 className="text-2xl font-bold text-blood-400 mb-6 tracking-wider">ARENA DISTRIBUTION</h2>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={getWorkoutTypeData()}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                    <XAxis dataKey="name" stroke="#737373" tick={{ fill: '#a3a3a3' }} />
                    <YAxis stroke="#737373" tick={{ fill: '#a3a3a3' }} />
                    <Tooltip
                      contentStyle={{ 
                        backgroundColor: '#171717', 
                        border: '1px solid #7f1d1d',
                        borderRadius: '8px'
                      }}
                      labelStyle={{ color: '#dc2626' }}
                      itemStyle={{ color: '#dc2626' }}
                    />
                    <Bar dataKey="count" fill="#dc2626" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </Card>
            )}
          </TabsContent>

          {/* Charts Tab */}
          <TabsContent value="charts" className="space-y-6" data-testid="charts-content">
            {stats && Object.keys(stats.exercise_progress).length > 0 ? (
              <>
                {/* Exercise Selector */}
                <Card className="glass p-6 border border-stone-800">
                  <h3 className="text-blood-400 font-bold text-lg mb-4 tracking-wider uppercase">Select Movement</h3>
                  <div className="flex flex-wrap gap-2">
                    {Object.keys(stats.exercise_progress).map((exerciseName) => (
                      <Button
                        key={exerciseName}
                        variant={selectedExercise === exerciseName ? 'default' : 'outline'}
                        onClick={() => setSelectedExercise(exerciseName)}
                        className={selectedExercise === exerciseName 
                          ? 'bg-gradient-to-r from-blood-700 to-blood-900 hover:from-blood-600 hover:to-blood-800 text-white font-semibold' 
                          : 'glass border-blood-700 text-blood-400 hover:bg-blood-950/50'
                        }
                        data-testid={`exercise-btn-${exerciseName}`}
                      >
                        {exerciseName}
                      </Button>
                    ))}
                  </div>
                </Card>

                {/* Progress Chart */}
                <Card className="glass p-6 border border-stone-800">
                  <h2 className="text-2xl font-bold text-blood-400 mb-6 tracking-wider uppercase">
                    {selectedExercise} - Power Evolution ⚔️
                  </h2>
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={getChartData()}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#404040" />
                      <XAxis dataKey="date" stroke="#737373" tick={{ fill: '#a3a3a3', fontSize: 12 }} angle={-45} textAnchor="end" height={80} />
                      <YAxis stroke="#737373" tick={{ fill: '#a3a3a3' }} label={{ value: `Weight (${user?.weight_unit || 'lbs'})`, angle: -90, position: 'insideLeft', fill: '#dc2626' }} />
                      <Tooltip
                        contentStyle={{ 
                          backgroundColor: '#171717', 
                          border: '1px solid #7f1d1d',
                          borderRadius: '8px'
                        }}
                        labelStyle={{ color: '#dc2626' }}
                        itemStyle={{ color: '#dc2626' }}
                      />
                      <Legend wrapperStyle={{ color: '#dc2626' }} />
                      <Line
                        type="monotone"
                        dataKey="weight"
                        stroke="#dc2626"
                        strokeWidth={3}
                        dot={{ fill: '#dc2626', r: 6, strokeWidth: 2, stroke: '#ffffff' }}
                        activeDot={{ r: 8 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </Card>
              </>
            ) : (
              <Card className="glass p-12 text-center border border-stone-800">
                <TrendingUp className="w-16 h-16 text-blood-600 mx-auto mb-4 opacity-50" />
                <p className="text-white text-xl mb-2 font-bold tracking-wider">NO PROGRESS DATA YET</p>
                <p className="text-stone-400 uppercase tracking-wide">Complete battles to see your evolution</p>
              </Card>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="space-y-4" data-testid="history-content">
            {history.length > 0 ? (
              history.map((workout, idx) => (
                <Card key={idx} className="glass p-6 card-hover border border-stone-800" data-testid={`history-item-${idx}`}>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-xl font-bold text-blood-400 mb-1 tracking-wide">
                        {new Date(workout.date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }).toUpperCase()}
                      </h3>
                      <p className="text-stone-400 uppercase tracking-wide text-sm">
                        {getWorkoutTypeLabel(workout.workout_type)}
                      </p>
                    </div>
                    {workout.completed && (
                      <span className="px-3 py-1 bg-blood-900/30 border border-blood-700 rounded-full text-blood-400 text-sm font-semibold uppercase tracking-wide">
                        ✓ Done
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {workout.exercises.map((ex, exIdx) => (
                      <div key={exIdx} className="glass p-3 rounded-lg border border-stone-800">
                        <div className="flex justify-between items-center">
                          <span className="text-white font-semibold tracking-wide">{ex.exercise_name}</span>
                          <span className="text-blood-400 text-sm font-semibold">
                            {ex.sets.filter(s => s.completed).length} sets ⚔
                          </span>
                        </div>
                        <div className="text-stone-400 text-sm mt-1">
                          Last set: {ex.sets[ex.sets.length - 1]?.weight || 0} {user?.weight_unit || 'lbs'} x {ex.sets[ex.sets.length - 1]?.reps || 0} reps
                        </div>
                      </div>
                    ))}
                  </div>
                  {workout.notes && (
                    <div className="mt-4 glass p-3 rounded-lg border border-blood-800">
                      <p className="text-blood-400 text-sm font-bold mb-1 uppercase tracking-wide">Battle Notes:</p>
                      <p className="text-stone-300 text-sm">{workout.notes}</p>
                    </div>
                  )}
                </Card>
              ))
            ) : (
              <Card className="glass p-12 text-center border border-stone-800">
                <Calendar className="w-16 h-16 text-blood-600 mx-auto mb-4 opacity-50" />
                <p className="text-white text-xl mb-2 font-bold tracking-wider">NO BATTLE HISTORY YET</p>
                <p className="text-stone-400 uppercase tracking-wide">Start training to build your progress history</p>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default Progress;
